import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import DropdownButton from '../../../components/Tools/DropdownButton';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { RiPencilLine, RiDownload2Fill } from 'react-icons/ri';
import { FaEye, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../ui/button';
import { LoadingIndicator } from '../../../components/Tools/LoadingIndicator';
import DisplayError from '../../../components/Tools/DisplayError';
import { BiSolidVideoRecording } from 'react-icons/bi';
import { FaImage } from 'react-icons/fa6';

// Replace with your actual Pexels API key
const PEXELS_API_KEY = process.env.REACT_APP_PEXELS_API_KEY;
const CURATED_API_URL = 'https://api.pexels.com/v1/curated';
const SEARCH_API_URL = 'https://api.pexels.com/v1/search';
const VIDEOS_API_URL = 'https://api.pexels.com/videos/popular';
const VIDEOS_SEARCH_API_URL = 'https://api.pexels.com/videos/search';
const PER_PAGE = 20;

// Predefined groups for categorized content
const GROUPS = [
    'ugc entrepreneurs',
    'ugc couples', 
    'ugc laptop',
    'lifestyle',
    'summer',
    'school',
    'gym',
    'aesthetics books'
];

const StockLibrary = ({ 
    isPage = true, 
    onConfirm, 
    uploadType, 
    isInModal = false,
    multiSelect = false,
    // Filter props from parent Library component
    filterTitle = '',
    filterMediaType = 'All Files',
    // Grouped view props
    showGrouped = false,
    onToggleGrouped = () => {}
}) => {
    // Main state for images and loading
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);
    const { activeWorkspace } = useWorkspace();
    const dialogRef = useRef(null);
    const [selectedMedia, setSelectedMedia] = useState(null);
    const navigate = useNavigate();

    // NEW: State for selected files
    const [selectedFiles, setSelectedFiles] = useState([]);
    
    // NEW: State for grouped media functionality
    const [groupedMedia, setGroupedMedia] = useState({});
    const [groupedLoading, setGroupedLoading] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null); // null = show categories, string = show specific category
    const [categoryCurrentPage, setCategoryCurrentPage] = useState(1);
    const [categoryTotalResults, setCategoryTotalResults] = useState(0);

    // Function to fetch curated media (popular)
    const fetchCuratedMedia = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            // Fetch photos - prefer vertical orientation for social media
            const photosResponse = await fetch(`${CURATED_API_URL}?per_page=${PER_PAGE}&page=${page}&orientation=portrait`, {
                headers: {
                    Authorization: PEXELS_API_KEY,
                },
            });
            const photosData = await photosResponse.json();
            const photos = photosData.photos || [];
            
            // Add type property to photos for easier identification
            const photosWithType = photos.map(photo => ({
                ...photo,
                type: 'photo'
            }));
            
            // Fetch videos
            const videosResponse = await fetch(`${VIDEOS_API_URL}?per_page=${PER_PAGE}&page=${page}`, {
                headers: {
                    Authorization: PEXELS_API_KEY,
                },
            });
            const videosData = await videosResponse.json();
            const videos = videosData.videos || [];
            
            // Add type property to videos for easier identification
            const videosWithType = videos.map(video => ({
                ...video,
                type: 'video'
            }));
            
            // Combine photos and videos
            const combinedMedia = [...photosWithType, ...videosWithType];
            setMedia(combinedMedia);
            setTotalResults((photosData.total_results || 0) + (videosData.total_results || 0));
            setCurrentPage(page);
        } catch (err) {
            console.error('Error fetching curated media:', err);
            setError('Failed to fetch stock media.');
        }
        setLoading(false);
    }, []);

    // Function to search for media based on a query and filters
    const searchMedia = useCallback(async (query, page = 1) => {
        setLoading(true);
        setError(null);
        
        // Build query parameters
        let params = new URLSearchParams();
        params.append('query', query);
        params.append('per_page', PER_PAGE);
        params.append('page', page);
        
        try {
            // Search photos - prefer vertical orientation
            params.append('orientation', 'portrait');
            const photosResponse = await fetch(`${SEARCH_API_URL}?${params.toString()}`, {
                headers: {
                    Authorization: PEXELS_API_KEY,
                },
            });
            const photosData = await photosResponse.json();
            const photos = photosData.photos || [];
            
            // Add type property to photos
            const photosWithType = photos.map(photo => ({
                ...photo,
                type: 'photo'
            }));
            
            // Search videos
            const videosResponse = await fetch(`${VIDEOS_SEARCH_API_URL}?${params.toString()}`, {
                headers: {
                    Authorization: PEXELS_API_KEY,
                },
            });
            const videosData = await videosResponse.json();
            const videos = videosData.videos || [];
            
            // Add type property to videos
            const videosWithType = videos.map(video => ({
                ...video,
                type: 'video'
            }));
            
            // Combine photos and videos
            const combinedMedia = [...photosWithType, ...videosWithType];
            setMedia(combinedMedia);
            setTotalResults((photosData.total_results || 0) + (videosData.total_results || 0));
            setCurrentPage(page);
        } catch (err) {
            console.error('Error searching media:', err);
            setError('Failed to fetch media for your search query.');
        }
        setLoading(false);
    }, []);

    // Function to fetch grouped media previews (4 images per category for cards)
    const fetchGroupedMedia = useCallback(async () => {
        setGroupedLoading(true);
        setError(null);
        
        try {
            const results = await Promise.all(
                GROUPS.map(async (topic) => {
                    const photosResponse = await fetch(`${SEARCH_API_URL}?query=${encodeURIComponent(topic)}&per_page=4&orientation=portrait`, {
                        headers: {
                            Authorization: PEXELS_API_KEY,
                        },
                    });
                    const photosData = await photosResponse.json();
                    const photos = (photosData.photos || []).map(photo => ({
                        ...photo,
                        type: 'photo'
                    }));
                    
                    return [topic, photos];
                })
            );
            
            // Convert array of [topic, photos] to object
            const mediaObject = Object.fromEntries(results);
            setGroupedMedia(mediaObject);
        } catch (err) {
            console.error('Error fetching grouped media:', err);
            setError('Failed to fetch grouped media.');
        } finally {
            setGroupedLoading(false);
        }
    }, []);

    // Function to fetch more images for a specific category with pagination
    const fetchCategoryMedia = useCallback(async (topic, page = 1, append = false) => {
        setGroupedLoading(true);
        setError(null);
        
        try {
            const photosResponse = await fetch(`${SEARCH_API_URL}?query=${encodeURIComponent(topic)}&per_page=20&page=${page}&orientation=portrait`, {
                headers: {
                    Authorization: PEXELS_API_KEY,
                },
            });
            const photosData = await photosResponse.json();
            const photos = (photosData.photos || []).map(photo => ({
                ...photo,
                type: 'photo'
            }));
            
            // Update the selected category with more images
            setGroupedMedia(prev => ({
                ...prev,
                [topic]: append ? [...(prev[topic] || []), ...photos] : photos
            }));
            
            // Update pagination state
            setCategoryTotalResults(photosData.total_results || 0);
            setCategoryCurrentPage(page);
        } catch (err) {
            console.error('Error fetching category media:', err);
            setError('Failed to fetch category media.');
        } finally {
            setGroupedLoading(false);
        }
    }, []);

    // Toggle between grouped and normal view
    const toggleGroupedView = () => {
        onToggleGrouped();
        setSelectedCategory(null); // Reset category selection
    };

    // Fetch grouped media when showGrouped becomes true
    useEffect(() => {
        if (showGrouped && Object.keys(groupedMedia).length === 0) {
            fetchGroupedMedia();
        }
    }, [showGrouped, groupedMedia, fetchGroupedMedia]);

    // Handle category selection
    const handleCategoryClick = (topic) => {
        setSelectedCategory(topic);
        setCategoryCurrentPage(1); // Reset pagination
        // If we only have preview images (4), fetch more for this category
        if (groupedMedia[topic] && groupedMedia[topic].length <= 4) {
            fetchCategoryMedia(topic, 1, false);
        }
    };

    // Go back to category overview
    const handleBackToCategories = () => {
        setSelectedCategory(null);
        setCategoryCurrentPage(1);
        setCategoryTotalResults(0);
    };

    // Handle pagination for category view
    const handleCategoryPageChange = (newPage) => {
        if (selectedCategory) {
            fetchCategoryMedia(selectedCategory, newPage, false);
        }
    };

    // Initial load of curated media
    useEffect(() => {
        fetchCuratedMedia(1);
    }, [fetchCuratedMedia]);

    // Handle the search based on filter changes
    useEffect(() => {
        if (filterTitle) {
            setSearchTerm(filterTitle);
            searchMedia(filterTitle, currentPage);
        } else if (searchTerm) {
            searchMedia(searchTerm, currentPage);
        }
    }, [filterTitle, searchTerm, currentPage, filterMediaType, searchMedia]);

    const handleCloseDialog = () => {
        if (dialogRef.current && dialogRef.current.open) {
            dialogRef.current.close();
        }
        setSelectedMedia(null);
    };

    // Update the click outside handler for dialog.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && dialogRef.current.open && !dialogRef.current.contains(event.target)) {
                setSelectedMedia(null);
                dialogRef.current.close();
            }
        };

        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, []);

    // Helper functions for file type checks
    const isVideo = (item) => item.type === 'video';
    const isImage = (item) => item.type === 'photo';

    // Action handlers for individual file actions
    const handleEditButtonClick = (item) => {
        const uploadType = isVideo(item) ? "video" : "image";
        const fileData = {
            posts: [
                {
                    uploadType,
                    video_path: item.src.original,
                    firebase_path: item.src.original,
                },
            ],
        };
        navigate(`/dashboard/upload`, { state: { upload: fileData } });
    };

    const handleViewButtonClick = (item) => {
        setSelectedMedia(item);
        if (dialogRef.current) {
            dialogRef.current.showModal();
        }
    };

    const handleDownloadButtonClick = (item) => {
        // For Pexels, just open the original URL in a new tab
        if (isVideo(item)) {
            // Find the highest quality video file
            const videoFile = item.video_files.reduce((highest, current) => 
                (current.height > highest.height) ? current : highest, 
                item.video_files[0]
            );
            window.open(videoFile.link, '_blank');
        } else {
            window.open(item.src.original, '_blank');
        }
    };



    // Update the file selection toggle to handle both single and multi-select modes
    const toggleFileSelection = (file) => {
        if (!isPage && !multiSelect) {
            // Single selection mode for modal (default behavior)
            setSelectedFiles([file]);
        } else if (!isPage && multiSelect) {
            // Multi-select mode for modal
            setSelectedFiles((prev) => {
                const alreadySelected = prev.some((f) => f.id === file.id);
                if (alreadySelected) {
                    return prev.filter((f) => f.id !== file.id);
                } else {
                    return [...prev, file];
                }
            });
        } else {
            // Page mode (always multi-select)
            setSelectedFiles((prev) => {
                const alreadySelected = prev.some((f) => f.id === file.id);
                if (alreadySelected) {
                    return prev.filter((f) => f.id !== file.id);
                } else {
                    return [...prev, file];
                }
            });
        }
    };

    // NEW: Multi-select actions
    const handleDownloadSelected = () => {
        selectedFiles.forEach((file) => {
            handleDownloadButtonClick(file);
        });
    };

    const cancelMultiSelect = () => {
        setSelectedFiles([]);
    };

    // Add a confirm button handler
    const handleConfirm = () => {
        if (selectedFiles.length > 0) {
            if (multiSelect) {
                // Pass array of selected files for multiselect
                onConfirm(selectedFiles);
            } else {
                // Pass single file for single select (backward compatibility)
                onConfirm(selectedFiles[0]);
            }
        }
    };

    // Compute filtered media
    const filteredMedia = media.filter((item) => {
        // For videos, check video description instead of alt
        if (filterTitle) {
            if (isVideo(item)) {
                if (!item.url.toLowerCase().includes(filterTitle.toLowerCase())) {
                    return false;
                }
            } else {
                if (!item.alt.toLowerCase().includes(filterTitle.toLowerCase())) {
                    return false;
                }
            }
        }
        
        if (filterMediaType === 'Images' && !isImage(item)) {
            return false;
        }
        if (filterMediaType === 'Videos' && !isVideo(item)) {
            return false;
        }
        
        // Apply additional filter based on passed uploadType
        if (uploadType === 'video' && !isVideo(item)) {
            return false;
        }
        if ((uploadType === 'image' || uploadType === 'post') && !isImage(item)) {
            return false;
        }
        
        // Note: For StockLibrary, we can't filter by time uploaded as the API doesn't provide this data
        // We'll keep the filter in the UI for consistency but it won't affect results
        
        return true;
    });




    return (
        <div className="dashboard-container flex flex-col w-full px-2 sm:px-4 relative">

            {/* Media Grid or Loading Indicator */}
            {(loading || groupedLoading) ? (
                <div className="flex justify-center items-center h-screen">
                    <LoadingIndicator showLogo={true} />
                </div>
            ) : error ? (
                <div className="flex justify-center items-center h-full max-w-[100%] w-[300px] mx-auto">
                    <DisplayError message={error} />
                </div>
            ) : showGrouped ? (
                /* Grouped Media View */
                Object.keys(groupedMedia).length === 0 ? (
                    <div className="flex justify-center items-center h-full max-w-[100%] w-[300px] mx-auto">
                        <DisplayError message="No grouped media found." />
                    </div>
                ) : selectedCategory ? (
                    /* Individual Category View */
                    <div>
                        {/* Back Button and Category Title */}
                        <div className="flex items-center mb-6">
                            <Button 
                                onClick={handleBackToCategories}
                                variant="outline" 
                                className="mr-4"
                            >
                                ← Back to Categories
                            </Button>
                            <h2 className="text-2xl font-bold capitalize !p-0 !m-0">
                                {selectedCategory.replace('ugc ', 'UGC ')}
                            </h2>
                        </div>
                        
                        {/* Category Images Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
                            {groupedMedia[selectedCategory]?.map((item) => {
                                const isSelected = selectedFiles.some((f) => f.id === item.id);
                                return (
                                    <div
                                        key={item.id}
                                        onClick={() => toggleFileSelection(item)}
                                        className={`cursor-pointer relative bg-gray-100 rounded-lg overflow-hidden aspect-[3/4] shadow hover:shadow-lg transition-shadow duration-200 flex flex-col justify-between ${isSelected ? 'border-2 sm:border-4 border-blue-500' : ''}`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <img
                                                src={item.src.medium}
                                                alt={item.alt}
                                                className="object-cover w-full h-full"
                                            />
                                            
                                            {/* Media Type Icon */}
                                            <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-black bg-opacity-50 rounded-full p-1 sm:p-1.5">
                                                <FaImage className="text-white" size={16} />
                                            </div>
                                            
                                            {/* Only show the dropdown if not in multi-select mode and isPage is true */}
                                            {!selectedFiles.length && isPage && (
                                                <DropdownButton
                                                    label={<BsThreeDotsVertical size={18} color="black" />}
                                                    classes="absolute top-1 sm:top-2 right-1 sm:right-2"
                                                    options={[
                                                        {
                                                            label: 'View',
                                                            onClick: () => handleViewButtonClick(item),
                                                            icon: <FaEye size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />,
                                                        },
                                                        {
                                                            label: 'Create post',
                                                            onClick: () => handleEditButtonClick(item),
                                                            icon: (
                                                                <RiPencilLine size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                            ),
                                                        },
                                                        {
                                                            label: 'Download',
                                                            onClick: () => handleDownloadButtonClick(item),
                                                            icon: (
                                                                <RiDownload2Fill size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                            ),
                                                        },
                                                    ]}
                                                />
                                            )}
                                            
                                            {/* Attribution Badge */}
                                            <div className='flex flex gap-1 absolute bottom-1 sm:bottom-2 left-1 sm:left-2'>
                                                <div className="bg-black bg-opacity-50 text-white text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded-md">
                                                    Photo by {item.photographer}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        {/* Category Pagination Controls */}
                        {categoryTotalResults > 20 && (
                            <div className="flex justify-center mt-6 mb-4">
                                <Button 
                                    variant="outline" 
                                    className="mx-1" 
                                    onClick={() => handleCategoryPageChange(categoryCurrentPage - 1)}
                                    disabled={categoryCurrentPage === 1 || groupedLoading}
                                >
                                    Previous
                                </Button>
                                <span className="mx-2 flex items-center">
                                    Page {categoryCurrentPage} of {Math.ceil(categoryTotalResults / 20)}
                                </span>
                                <Button 
                                    variant="outline" 
                                    className="mx-1" 
                                    onClick={() => handleCategoryPageChange(categoryCurrentPage + 1)}
                                    disabled={categoryCurrentPage >= Math.ceil(categoryTotalResults / 20) || groupedLoading}
                                >
                                    Next
                                </Button>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Category Cards View */
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-4">
                        {Object.entries(groupedMedia).map(([topic, photos]) => (
                            <div
                                key={topic}
                                onClick={() => handleCategoryClick(topic)}
                                className="cursor-pointer bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                            >
                                {/* Category Preview - Single row of images */}
                                <div className="flex gap-0.5 aspect-[4/3]">
                                    {photos.slice(0, 4).map((photo, index) => (
                                        <div key={photo.id} className="relative overflow-hidden flex-1">
                                            <img
                                                src={photo.src.medium}
                                                alt={photo.alt}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                    {/* Fill empty slots if less than 4 images */}
                                    {Array.from({ length: Math.max(0, 4 - photos.length) }).map((_, index) => (
                                        <div key={`empty-${index}`} className="bg-gray-200 flex-1"></div>
                                    ))}
                                </div>
                                
                                {/* Category Title */}
                                <div className="p-2">
                                    <h3 className="text-sm font-semibold capitalize text-center">
                                        {topic.replace('ugc ', 'UGC ')}
                                    </h3>
                                    <p className="text-xs text-gray-500 text-center mt-1">
                                        {photos.length} images
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : filteredMedia.length === 0 ? (
                <div className="flex justify-center items-center h-full max-w-[100%] w-[300px] mx-auto">
                    <DisplayError message="No media found. Try another search." />
                </div>
            ) : (
                /* Normal Media Grid View */
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 py-2">
                    {filteredMedia.map((item) => {
                        // Determine if this file is selected in multi-select mode.
                        const isSelected = selectedFiles.some((f) => f.id === item.id);
                        return (
                            <div
                                key={item.id}
                                onClick={() => toggleFileSelection(item)}
                                className={`cursor-pointer relative bg-gray-100 rounded-lg overflow-hidden aspect-[3/4] shadow hover:shadow-lg transition-shadow duration-200 flex flex-col justify-between ${isSelected ? 'border-2 sm:border-4 border-blue-500' : ''}`}
                            >
                                <div className="flex justify-between items-start">
                                    {isVideo(item) ? (
                                        <video
                                            className="object-cover w-full h-full"
                                            src={item.video_files[0].link}
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                        />
                                    ) : (
                                        <img
                                            src={item.src.medium}
                                            alt={item.alt}
                                            className="object-cover w-full h-full"
                                        />
                                    )}
                                    
                                    {/* Media Type Icon */}
                                    <div className="absolute top-1 sm:top-2 left-1 sm:left-2 bg-black bg-opacity-50 rounded-full p-1 sm:p-1.5">
                                        {isVideo(item) ? (
                                            <BiSolidVideoRecording className="text-white" size={16} />
                                        ) : (
                                            <FaImage className="text-white" size={16} />
                                        )}
                                    </div>
                                    
                                    {/* Only show the dropdown if not in multi-select mode and isPage is true */}
                                    {!selectedFiles.length && isPage && (
                                        <DropdownButton
                                            label={<BsThreeDotsVertical size={18} color="black" />}
                                            classes="absolute top-1 sm:top-2 right-1 sm:right-2"
                                            options={[
                                                {
                                                    label: 'View',
                                                    onClick: () => handleViewButtonClick(item),
                                                    icon: <FaEye size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />,
                                                },
                                                {
                                                    label: 'Create post',
                                                    onClick: () => handleEditButtonClick(item),
                                                    icon: (
                                                        <RiPencilLine size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                    ),
                                                },
                                                {
                                                    label: 'Download',
                                                    onClick: () => handleDownloadButtonClick(item),
                                                    icon: (
                                                        <RiDownload2Fill size={16} className="ml-3 sm:ml-5 text-gray-600 font-bold" />
                                                    ),
                                                },
                                            ]}
                                        />
                                    )}
                                    
                                    {/* Attribution Badge */}
                                    <div className='flex flex gap-1 absolute bottom-1 sm:bottom-2 left-1 sm:left-2'>
                                        <div className="bg-black bg-opacity-50 text-white text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1 rounded-md">
                                            {isVideo(item) ? 'Video' : 'Photo'} by {item.photographer || item.user?.name}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination Controls - only show in normal view */}
            {!loading && !showGrouped && filteredMedia.length > 0 && (
                <div className="flex justify-center mt-4 mb-8">
                    <Button 
                        variant="outline" 
                        className="mx-1" 
                        onClick={() => currentPage > 1 && (searchTerm ? searchMedia(searchTerm, currentPage - 1) : fetchCuratedMedia(currentPage - 1))}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </Button>
                    <span className="mx-2 flex items-center">Page {currentPage}</span>
                    <Button 
                        variant="outline" 
                        className="mx-1" 
                        onClick={() => searchTerm ? searchMedia(searchTerm, currentPage + 1) : fetchCuratedMedia(currentPage + 1)}
                        disabled={media.length < PER_PAGE}
                    >
                        Next
                    </Button>
                </div>
            )}

            {/* View Media Dialog */}
            <dialog
                ref={dialogRef}
                className="w-full max-w-2xl p-0 flex items-center justify-center mx-auto my-auto"
                onClick={isInModal ? null : (e) => e.stopPropagation()}
            >
                {selectedMedia && (
                    <div className="relative bg-white p-5 rounded-lg">
                        <div onClick={handleCloseDialog} className="absolute top-2 right-2 text-black">
                            <FaTimes color="black" size={20} />
                        </div>
                        {isVideo(selectedMedia) ? (
                            <video 
                                src={selectedMedia.video_files[0].link} 
                                controls 
                                className="max-w-full max-h-[80vh]" 
                            />
                        ) : (
                            <img
                                src={selectedMedia.src.large}
                                alt={selectedMedia.alt}
                                className="max-w-full max-h-[80vh]"
                            />
                        )}
                        <div className="mt-2 text-sm text-gray-600">
                            {isVideo(selectedMedia) ? 'Video' : 'Photo'} by{' '}
                            <a 
                                href={selectedMedia.photographer_url || selectedMedia.user?.url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="underline"
                            >
                                {selectedMedia.photographer || selectedMedia.user?.name}
                            </a>{' '}
                            on <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels</a>
                        </div>
                    </div>
                )}
            </dialog>

            {/* Confirm Button at Bottom Right */}
            {!isPage && selectedFiles.length > 0 && (multiSelect || selectedFiles.length === 1) && (
                <div className="fixed bottom-4 right-4">
                    <Button size="xl" variant="default" onClick={handleConfirm}>
                       Confirm {multiSelect && selectedFiles.length > 1 ? `(${selectedFiles.length})` : ''}
                    </Button>
                </div>
            )}

            {/* Pexels Attribution */}
            <div className="text-center  mt-4 text-sm text-gray-500">
                Photos and videos provided by <a href="https://www.pexels.com" target="_blank" rel="noopener noreferrer" className="underline">Pexels</a>
            </div>
        </div>
    );
};



export default StockLibrary;
